import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { getStripeStatus, syncAllStripeModulePrices } from '../api/stripeApi';
import { colorTokens } from '../app/designTokens';
import { useAuth } from '../auth/AuthContext';
import { hasPermission } from '../auth/permissions';
import { DataTable } from '../components/DataTable';
import { DetailField } from '../components/InfoCard';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { StatusBadge } from '../components/StatusBadge';
import { Button } from '../components/ui/Button';
import { TextInput } from '../components/ui/TextInput';

const { semantic } = colorTokens;

const detailGridSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
  gap: 2.5,
  minWidth: 0,
  maxWidth: '100%',
  '& > *': { minWidth: 0, maxWidth: '100%' },
};

const monoSx = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  fontSize: 12,
  lineHeight: '18px',
  color: semantic.text.primary,
  minWidth: 0,
  maxWidth: '100%',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
};

const mutedSx = {
  fontSize: 13,
  lineHeight: '18px',
  color: semantic.text.secondary,
};

function resolveStripeMessage(payload, t, fallbackKey) {
  const rawKey = payload?.message_key;
  if (rawKey) {
    const i18nKey = String(rawKey).startsWith('stripe.')
      ? `stripe:${String(rawKey).slice('stripe.'.length)}`
      : rawKey;
    return t(i18nKey, { defaultValue: payload.message || t(fallbackKey) });
  }
  return payload?.message || t(fallbackKey);
}

function YesNoBadge({ value, t }) {
  return (
    <StatusBadge
      status={value ? 'yes' : 'no'}
      category="generic"
      apiLabel={value ? t('common:yes') : t('common:no')}
      size="Sm"
      tone={value ? 'Success' : 'Neutral'}
    />
  );
}

export function StripeDashboardPage() {
  const { t } = useTranslation(['stripe', 'common']);
  const { session } = useAuth();
  const canSync = hasPermission(session, 'system.stripe.sync');

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmation, setConfirmation] = useState('');
  const [actionMessage, setActionMessage] = useState('');
  const [actionError, setActionError] = useState('');
  const [bulkResult, setBulkResult] = useState(null);

  const emptyValue = t('common:empty_value');

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await getStripeStatus();
      setStatus(data);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const confirmationOk = confirmation.trim().toLowerCase() === 'sync-module-prices';
  const syncDisabled = !status?.enabled || !status?.has_secret_key || !status?.has_publishable_key || !canSync || busy;

  const resultColumns = useMemo(
    () => [
      {
        id: 'module_code',
        label: t('stripe:labels.moduleCode'),
        render: (row) => (
          <Typography sx={monoSx}>{row.module_code || emptyValue}</Typography>
        ),
      },
      {
        id: 'display_name',
        label: t('stripe:labels.displayName'),
        render: (row) => row.display_name || emptyValue,
      },
      {
        id: 'status',
        label: t('common:status'),
        render: (row) => {
          const label = row.status === 'synced'
            ? t('stripe:labels.synced')
            : row.status === 'failed'
              ? t('stripe:labels.failed')
              : t('stripe:labels.skipped');
          const tone = row.status === 'synced'
            ? 'Success'
            : row.status === 'failed'
              ? 'Danger'
              : 'Neutral';
          return (
            <StatusBadge
              status={row.status || 'skipped'}
              category="generic"
              apiLabel={label}
              size="Sm"
              tone={tone}
            />
          );
        },
      },
      {
        id: 'product_id',
        label: t('stripe:labels.product'),
        render: (row) => (
          row.product_id
            ? <Typography sx={monoSx}>{row.product_id}</Typography>
            : emptyValue
        ),
      },
      {
        id: 'monthly_price_id',
        label: t('stripe:labels.monthlyPrice'),
        render: (row) => (
          row.monthly_price_id
            ? <Typography sx={monoSx}>{row.monthly_price_id}</Typography>
            : emptyValue
        ),
      },
      {
        id: 'yearly_price_id',
        label: t('stripe:labels.yearlyPrice'),
        render: (row) => (
          row.yearly_price_id
            ? <Typography sx={monoSx}>{row.yearly_price_id}</Typography>
            : emptyValue
        ),
      },
      {
        id: 'message_key',
        label: t('stripe:sync_logs.columns.message'),
        render: (row) => resolveStripeMessage(row, t, 'stripe:messages.modulePricesSyncFailed'),
      },
    ],
    [emptyValue, t],
  );

  const runBulkSync = async () => {
    setBusy(true);
    setActionError('');
    setActionMessage('');
    try {
      const res = await syncAllStripeModulePrices({ confirmation: confirmation.trim() });
      const data = res.data || res;
      setBulkResult(data);
      setActionMessage(resolveStripeMessage(data, t, 'stripe:messages.modulePricesSynced'));
      setConfirmation('');
    } catch (err) {
      setActionError(resolveStripeMessage(err?.response?.data || {}, t, 'stripe:messages.modulePricesSyncFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <PageHeader
        type="List"
        title={t('stripe:dashboard.title')}
        subtitle={t('stripe:dashboard.subtitle')}
        actions={(
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
            <Button variant="Secondary" component={RouterLink} to="/stripe/webhooks">
              {t('stripe:dashboard.webhook_inbox')}
            </Button>
            <Button variant="Ghost" component={RouterLink} to="/stripe/sync-logs">
              {t('stripe:dashboard.sync_logs')}
            </Button>
          </Stack>
        )}
      />

      {loading ? (
        <LoadingSkeleton type="Detail" />
      ) : error ? (
        <SectionCard variant="Danger">
          <Alert
            severity="error"
            sx={{ bgcolor: 'transparent', p: 0 }}
            action={(
              <Button variant="Ghost" onClick={loadStatus} sx={{ minHeight: 32 }}>
                {t('common:retry')}
              </Button>
            )}
          >
            {t('common:load_failed')}
          </Alert>
        </SectionCard>
      ) : status ? (
        <Stack spacing={2.5}>
          {!status.enabled && (
            <Alert severity="warning">
              {t('stripe:dashboard.disabled_alert')}
            </Alert>
          )}

          {status.enabled && !status.has_webhook_secret && (
            <Alert severity="info">
              {t('stripe:dashboard.webhook_secret_missing')}
            </Alert>
          )}

          {status.enabled && status.mode === 'test' && (
            <Alert severity="info">
              {t('stripe:dashboard.test_mode')}
            </Alert>
          )}

          {actionMessage && <Alert severity="success">{actionMessage}</Alert>}
          {actionError && <Alert severity="error">{actionError}</Alert>}

          <SectionCard variant="Default" title={t('stripe:dashboard.integration_status')} sx={{ mb: 0 }}>
            <Box sx={detailGridSx}>
              <DetailField label={t('stripe:dashboard.enabled')}>
                <YesNoBadge value={Boolean(status.enabled)} t={t} />
              </DetailField>
              <DetailField label={t('stripe:dashboard.mode')}>
                <Typography sx={monoSx}>{status.mode || emptyValue}</Typography>
              </DetailField>
              <DetailField label={t('stripe:dashboard.secret_key_configured')}>
                <YesNoBadge value={Boolean(status.has_secret_key)} t={t} />
              </DetailField>
              <DetailField label={t('stripe:dashboard.publishable_key_configured')}>
                <YesNoBadge value={Boolean(status.has_publishable_key)} t={t} />
              </DetailField>
              <DetailField label={t('stripe:dashboard.webhook_secret_configured')}>
                <YesNoBadge value={Boolean(status.has_webhook_secret)} t={t} />
              </DetailField>
              <DetailField label={t('stripe:dashboard.sdk_available')}>
                {status.sdk_available
                  ? <YesNoBadge value t={t} />
                  : (
                    <Typography sx={mutedSx}>
                      {t('stripe:dashboard.sdk_fallback')}
                    </Typography>
                  )}
              </DetailField>
              <DetailField label={t('stripe:dashboard.process_webhooks_sync')}>
                <YesNoBadge value={Boolean(status.process_webhooks_sync)} t={t} />
              </DetailField>
              <DetailField
                label={t('stripe:dashboard.pending_webhook_events')}
                value={status.pending_webhook_count ?? 0}
              />
              <DetailField label={t('stripe:dashboard.last_webhook')}>
                <Typography sx={monoSx}>
                  {status.last_webhook_received_at || emptyValue}
                  {' '}
                  (
                  {status.last_webhook_event_type || t('common:not_available')}
                  )
                </Typography>
              </DetailField>
            </Box>

            {status.disabled_reason && (
              <Box sx={{ mt: 2 }}>
                <StatusBadge
                  status="disabled"
                  category="generic"
                  apiLabel={status.disabled_reason}
                  size="Sm"
                  tone="Warning"
                />
              </Box>
            )}

            {!canSync && (
              <Alert severity="info" sx={{ mt: 2 }}>
                {t('stripe:dashboard.sync_permission_required')}
              </Alert>
            )}
          </SectionCard>

          {canSync && (
            <SectionCard variant="Default" title={t('stripe:actions.syncAllModulePrices')} sx={{ mb: 0 }}>
              <Typography sx={{ ...mutedSx, mb: 2 }}>
                {t('stripe:help.modulePriceSync')}
              </Typography>
              <Stack spacing={1.5} sx={{ mb: 2 }}>
                <Alert severity="info">{t('stripe:warnings.smsUsageSeparate')}</Alert>
                <Alert severity="info">{t('stripe:warnings.crmNotPublicBilling')}</Alert>
              </Stack>
              <Box sx={{ maxWidth: 360, mb: 2 }}>
                <TextInput
                  density="Compact"
                  label={t('stripe:confirmations.syncModulePrices')}
                  helperText="sync-module-prices"
                  value={confirmation}
                  onChange={(e) => setConfirmation(e.target.value)}
                />
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                <Button
                  variant="Accent"
                  disabled={syncDisabled || !confirmationOk}
                  onClick={runBulkSync}
                >
                  {t('stripe:actions.syncAllModulePrices')}
                </Button>
                <Button variant="Ghost" component={RouterLink} to="/stripe/sync-logs">
                  {t('stripe:dashboard.sync_logs')}
                </Button>
              </Stack>

              {bulkResult?.counts && (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                  <StatusBadge
                    status="synced"
                    category="generic"
                    apiLabel={`${t('stripe:labels.synced')}: ${bulkResult.counts.synced ?? 0}`}
                    size="Sm"
                    tone="Success"
                  />
                  <StatusBadge
                    status="skipped"
                    category="generic"
                    apiLabel={`${t('stripe:labels.skipped')}: ${bulkResult.counts.skipped ?? 0}`}
                    size="Sm"
                    tone="Neutral"
                  />
                  <StatusBadge
                    status="failed"
                    category="generic"
                    apiLabel={`${t('stripe:labels.failed')}: ${bulkResult.counts.failed ?? 0}`}
                    size="Sm"
                    tone="Danger"
                  />
                </Stack>
              )}

              {Array.isArray(bulkResult?.results) && bulkResult.results.length > 0 && (
                <SectionCard variant="Default" noPadding sx={{ mb: 0 }}>
                  <DataTable
                    type="Compact"
                    embedded
                    columns={resultColumns}
                    rows={bulkResult.results}
                    getRowKey={(row) => `${row.module_code}-${row.status}`}
                    emptyMessage={emptyValue}
                    sx={{
                      overflowX: 'auto',
                      width: '100%',
                      '& table': {
                        width: '100%',
                        minWidth: '100%',
                      },
                    }}
                  />
                </SectionCard>
              )}
            </SectionCard>
          )}
        </Stack>
      ) : null}
    </Box>
  );
}
